/**
 * Builds a switch URL for Morpheus domain or IDM org switching, optionally preserving the current route.
 * @param {string} appAddress - The base address of the target application.
 * @param {string} queryKey - The query parameter key to use for switching (e.g., '!domain' or 'currentOrg').
 * @param {string} queryValue - The value to set for the switching query parameter.
 * @param {Object} [options] - Additional options.
 * @param {boolean} [options.preserveRoute=false] - Whether to preserve the current route in the switch URL.
 * @param {string} [options.currentHref] - The current full URL to use for route preservation (defaults to window.location.href).
 * @returns {string} The constructed switch URL.
 */
function buildSwitchHref(appAddress, queryKey, queryValue, options = {}) {
	const { preserveRoute = false, currentHref, preserveRouteMode = 'underBasePath' } = options;
	const encodedValue = encodeURIComponent(queryValue);
	const legacyHref = `${appAddress}?${queryKey}=${encodedValue}`;

	if (!preserveRoute) {
		return legacyHref;
	}

	try {
		const resolvedCurrentHref = currentHref || window.location.href;
		const currentUrl = new URL(resolvedCurrentHref, window.location.origin);
		const destinationUrl = new URL(appAddress, currentUrl.origin);

		const currentPath = normalizePathname(currentUrl.pathname);
		if (preserveRouteMode === 'sameOrigin') {
			// For IDM org switching, accessRecord.Url may point at a "landing" page
			// (e.g., /ec-dashboard/). When switching orgs within the same origin,
			// preserve the current route regardless of the destination pathname.
			if (destinationUrl.origin === currentUrl.origin && currentPath) {
				destinationUrl.pathname = currentUrl.pathname;
			}
		} else {
			// Default behavior: only preserve the route if the current path is under
			// the appAddress base path.
			const basePath = normalizePathname(destinationUrl.pathname);
			const isUnderBasePath = basePath === '/'
				? true
				: (currentPath === basePath || currentPath.startsWith(`${basePath}/`));
			if (currentPath && isUnderBasePath) {
				destinationUrl.pathname = currentUrl.pathname;
			}
		}

		// Only set the switching query param; do not preserve any other query params.
		destinationUrl.search = `?${queryKey}=${encodedValue}`;
		// Preserve hash as-is (it may contain route info for hash-based apps).
		destinationUrl.hash = currentUrl.hash;

		return destinationUrl.toString();
	} catch (e) {
		return legacyHref;
	}
}

/**
 * Builds an Morpheus domain switch URL, optionally preserving the current route.
 * Query param used: `!domain`
 */
export const buildDomainSwitchHref = (appAddress, selectedDomainName, options = {}) => (
	buildSwitchHref(appAddress, '!domain', selectedDomainName, options)
);

/**
 * Builds an IDM org switch URL, optionally preserving the current route.
 * Query param used: `currentOrg`
 */
export const buildOrgSwitchHref = (appAddress, currentOrg, options = {}) => (
	buildSwitchHref(appAddress, 'currentOrg', currentOrg, Object.assign({ preserveRouteMode: 'sameOrigin' }, options))
);

/**
 * Normalizes a pathname by removing trailing slashes (except for root).
 * @param {string} pathname - The pathname to normalize.
 * @returns {string} The normalized pathname.
 */
function normalizePathname(pathname) {
	if (!pathname) {
		return '/';
	}
	return pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
}
